import { useForm } from 'antd/es/form/Form';
import { useCallback, useContext, useState } from 'react';

import {
  useLazyGetIncomesReportQuery,
  type ModelIncomesReport,
} from '@app/api/Analytics';
import { REVENUE_STATEMENT } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { DEFAULT_FILTERS_DATA } from '@app/pages/Analytics/data';
import { useShownReportPeriod } from '@app/pages/Analytics/hooks';
import type { FiltersInterface } from '@app/pages/Analytics/RevenueStatement/types';
import type { HandleSearchDataProps } from '@app/pages/Analytics/types';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

export const useRevenueStatement = () => {
  const [tableData, setTableData] = useState<ModelIncomesReport>();

  const [form] = useForm<FiltersInterface>();

  const { setShownPeriod } = useShownReportPeriod(REVENUE_STATEMENT);
  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [getIncomesReport, { isLoading }] = useLazyGetIncomesReportQuery();

  const handleSearchData = useCallback(
    async ({ filterMonth, filterYear }: HandleSearchDataProps) => {
      try {
        const result = await getIncomesReport({
          ActiveBranchId,
          filterMonth,
          filterYear,
        }).unwrap();

        setTableData(result);
        setShownPeriod({ filterMonth, filterYear });
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, getIncomesReport, setShownPeriod, showNotification]
  );

  const handleReset = useCallback(() => {
    form.resetFields();

    handleSearchData(DEFAULT_FILTERS_DATA);
  }, [form, handleSearchData]);

  const handleSubmitForm = useCallback(
    (values: FiltersInterface) => {
      const { filterMonth, filterYear } = values;
      handleSearchData({
        filterMonth,
        filterYear,
      });
    },
    [handleSearchData]
  );

  return {
    handleReset,
    form,
    handleSubmitForm,
    handleSearchData,
    tableData,
    isLoading,
  };
};
